import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AddressSheet from "../components/AddressSheet";
import BottomNav from "../components/BottomNav";
import { useAppState } from "../context/AppState";
import { useLocale } from "../context/Locale";
import { api, authenticatedApi } from "../lib/api";
import { cartSubtotal, deliveryQuote, distanceKm, money } from "../lib/orderMath";

export default function CartPage() {
  const navigate = useNavigate();
  const {
    address,
    cart,
    changeCartQuantity,
    clearCart,
    openAddress,
    selectAddress,
    user,
  } = useAppState();
  const { t } = useLocale();
  const [restaurant, setRestaurant] = useState(null);
  const [comment, setComment] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [locationCheck, setLocationCheck] = useState({ status: "idle" });
  const [fulfillmentType, setFulfillmentType] = useState("delivery");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cart.length) {
      setRestaurant(null);
      return;
    }
    api(`/get-restaurant-info/${cart[0].restaurantSlug}`, {
      method: "POST",
      body: "{}",
    }).then((store) => {
      setRestaurant(store);
      if (Number(store?.delivery_type) !== 3) setFulfillmentType("delivery");
    }).catch((requestError) => setError(requestError.message));
  }, [cart]);

  useEffect(() => {
    setAddressConfirmed(false);
    setLocationCheck({ status: "idle" });
  }, [address?.lat, address?.lng]);

  const subtotal = useMemo(() => cartSubtotal(cart), [cart]);
  const quote = useMemo(() => deliveryQuote(restaurant, address), [address, restaurant]);
  const pickupAvailable = Number(restaurant?.delivery_type) === 3;
  const isPickup = pickupAvailable && fulfillmentType === "pickup";
  const deliveryCharge = isPickup ? 0 : quote.charge;
  const restaurantCharge = Number(restaurant?.restaurant_charges || 0);
  const discount = coupon
    ? coupon.discount_type === "PERCENTAGE"
      ? Math.min(
          subtotal * (Number(coupon.discount || 0) / 100),
          Number(coupon.max_discount || Number.MAX_SAFE_INTEGER),
        )
      : Math.min(subtotal, Number(coupon.discount || 0))
    : 0;
  const total = Math.max(0, subtotal + deliveryCharge + restaurantCharge - discount);

  async function applyCoupon() {
    if (!couponCode.trim() || !restaurant) return;
    if (!user?.auth_token) {
      navigate("/account?next=/cart");
      return;
    }
    setCouponBusy(true);
    setError("");
    try {
      const response = await authenticatedApi("/apply-coupon", user.auth_token, {
        method: "POST",
        body: JSON.stringify({
          coupon: couponCode.trim().toUpperCase(),
          restaurant_id: restaurant.id,
          subtotal,
        }),
      });
      if (!response?.success) throw new Error(response?.message || t("couponInvalid"));
      setCoupon(response);
    } catch (requestError) {
      setCoupon(null);
      setError(requestError.message);
    } finally {
      setCouponBusy(false);
    }
  }

  function verifyAddressWithGps() {
    if (!address) {
      openAddress();
      return;
    }
    if (!navigator.geolocation) {
      setLocationCheck({ status: "unavailable", reason: t("gpsUnsupported") });
      return;
    }

    setLocationCheck({ status: "checking" });
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      const currentPoint = {
        lat: Number(coords.latitude),
        lng: Number(coords.longitude),
        accuracy: Math.max(0, Number(coords.accuracy || 0)),
      };
      const distance = distanceKm(
        { lat: Number(address.lat), lng: Number(address.lng) },
        currentPoint,
      );
      const reliable = currentPoint.accuracy <= 250;
      const allowedDistance = Math.max(0.25, (currentPoint.accuracy / 1000) + 0.1);

      if (reliable && distance <= allowedDistance) {
        setAddressConfirmed(true);
        setLocationCheck({ status: "matched", distance, accuracy: currentPoint.accuracy });
        return;
      }
      setLocationCheck({
        status: reliable ? "mismatch" : "imprecise",
        distance,
        currentPoint,
        accuracy: currentPoint.accuracy,
      });
    }, (gpsError) => {
      setLocationCheck({
        status: "unavailable",
        reason: gpsError.code === 1 ? t("gpsPermissionRequired") : t("gpsUnavailableHelp"),
      });
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  }

  async function adjustAddressFromCurrentGps() {
    const point = locationCheck.currentPoint;
    if (!point) {
      openAddress();
      return;
    }
    let label = `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;
    try {
      const result = await api("/coordinate-to-address", {
        method: "POST",
        body: JSON.stringify({ lat: point.lat, lng: point.lng }),
      });
      label = (typeof result === "string" ? result : result?.address) || label;
    } catch {
      // El punto GPS sigue siendo válido aunque no se obtenga el nombre de la calle.
    }
    selectAddress({ ...point, address: label });
    window.setTimeout(openAddress, 0);
  }

  function confirmSelectedAddressAnyway() {
    setAddressConfirmed(true);
    setLocationCheck((current) => ({ ...current, status: "overridden" }));
  }

  function continueToPayment() {
    if (!isPickup && !address) {
      openAddress();
      return;
    }
    if (!isPickup && !addressConfirmed) return;
    sessionStorage.setItem("deliveryCheckoutDraft", JSON.stringify({
      comment: comment.trim(),
      coupon,
      couponCode: couponCode.trim().toUpperCase(),
      fulfillmentType: isPickup ? "pickup" : "delivery",
      addressVerification: isPickup ? null : {
        verifiedAt: Date.now(),
        latitude: Number(address.lat),
        longitude: Number(address.lng),
        method: locationCheck.status === "matched" ? "gps" : "customer_confirmation",
      },
    }));
    navigate("/checkout");
  }

  return (
    <main className="legacy-flow-page cart-flow">
      <header className="flow-header">
        <Link to="/food" aria-label={t("backStores")}>‹</Link>
        <strong>{t("cart")}</strong>
      </header>

      {cart.length === 0 ? (
        <section className="empty-cart">
          <span>🛍️</span>
          <h2>{t("emptyCart")}</h2>
          <p>{t("emptyCartHelp")}</p>
          <Link to="/food">{t("browseStores")}</Link>
        </section>
      ) : (
        <div className="legacy-cart">
          {restaurant && (
            <Link className="legacy-cart__store" to={`/food/stores/${cart[0].restaurantSlug}`}>
              <img src={restaurant.image} alt={restaurant.name} />
              <div>
                <h2>{restaurant.name}</h2>
                <p>{restaurant.description}</p>
                <span>★ {restaurant.avgRating || restaurant.rating || "0"}</span>
                <span>◷ {restaurant.delivery_time || "20"} min</span>
              </div>
            </Link>
          )}

          {pickupAvailable && (
            <section className="fulfillment-choice" aria-label={t("fulfillmentQuestion")}>
              <h2>{t("fulfillmentQuestion")}</h2>
              <div>
                <button
                  type="button"
                  className={!isPickup ? "is-selected" : ""}
                  aria-pressed={!isPickup}
                  onClick={() => setFulfillmentType("delivery")}
                >
                  <span aria-hidden="true">🛵</span>
                  <b>{t("deliveryOption")}</b>
                  <small>{t("deliveryOptionHelp")}</small>
                </button>
                <button
                  type="button"
                  className={isPickup ? "is-selected" : ""}
                  aria-pressed={isPickup}
                  onClick={() => setFulfillmentType("pickup")}
                >
                  <span aria-hidden="true">🏪</span>
                  <b>{t("pickupOption")}</b>
                  <small>{t("pickupOptionHelp")}</small>
                </button>
              </div>
            </section>
          )}

          <div className="legacy-cart__section-title">
            <h1>{t("cartProductsTitle")}</h1>
            <button type="button" onClick={clearCart}>{t("clearCart")}</button>
          </div>

          <section className="legacy-cart__lines">
            {cart.map((line) => {
              const addons = (line.selectedAddons || []).reduce(
                (sum, addon) => sum + Number(addon.price || 0),
                0,
              );
              return (
                <article key={line.lineId}>
                  <div>
                    <strong>{line.name}</strong>
                    {(line.selectedAddons || []).map((addon) => (
                      <small key={`${line.lineId}-${addon.addon_id}`}>
                        {addon.addon_name} - {money(addon.price)}
                      </small>
                    ))}
                  </div>
                  <div className="legacy-quantity">
                    <button
                      type="button"
                      aria-label={t("decreaseQuantity")}
                      onClick={() => changeCartQuantity(line.lineId, line.quantity - 1)}
                    >
                      {line.quantity === 1 ? "⌫" : "−"}
                    </button>
                    <b>{line.quantity}</b>
                    <button
                      type="button"
                      aria-label={t("increaseQuantity")}
                      onClick={() => changeCartQuantity(line.lineId, line.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <b>{money((Number(line.price || 0) + addons) * line.quantity)}</b>
                </article>
              );
            })}
          </section>

          <textarea
            className="legacy-cart__comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={t("cartCommentPlaceholder")}
          />

          <div className="legacy-coupon">
            <input
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value)}
              placeholder={t("coupon")}
            />
            <button type="button" onClick={applyCoupon} disabled={couponBusy}>
              {couponBusy ? "…" : t("applyCoupon")}
            </button>
          </div>
          {coupon && <p className="legacy-coupon__success">✓ {t("discountApplied")}: {money(discount)}</p>}
          {error && <p className="checkout-error">{error}</p>}

          <section className="legacy-bill">
            <h2>{t("paymentDetails")}</h2>
            <div><span>{t("products")}</span><b>{money(subtotal)}</b></div>
            <div>
              <span>
                {isPickup ? t("pickupAtStore") : t("deliveryFee")}
                {!isPickup && <small> ({quote.distance.toFixed(1)} km)</small>}
              </span>
              <b>{isPickup ? t("noCost") : money(deliveryCharge)}</b>
            </div>
            {restaurantCharge > 0 && <div><span>{t("businessCharge")}</span><b>{money(restaurantCharge)}</b></div>}
            {discount > 0 && <div className="is-discount"><span>{t("discount")}</span><b>− {money(discount)}</b></div>}
            <div className="legacy-bill__total"><span>{t("totalToPay")}</span><b>{money(total)}</b></div>
          </section>

          {isPickup ? (
            <section className="pickup-confirm">
              <span aria-hidden="true">🏪</span>
              <div>
                <h2>{t("pickupAtStore")}</h2>
                <strong>{restaurant?.name}</strong>
                <p>{restaurant?.address || t("pickupAddressHelp")}</p>
                <small>{t("pickupReadyHelp")}</small>
              </div>
            </section>
          ) : (
            <section className="legacy-address-confirm">
              <button type="button" className="legacy-address-confirm__change" onClick={openAddress}>
                {t("change")}
              </button>
              <h2>{t("confirmDeliveryAddress")}</h2>
              <strong>{address?.address || t("selectAnAddress")}</strong>
              <p>{t("verifyCurrentAddress")}</p>
              <button
                type="button"
                className={addressConfirmed ? "is-confirmed" : ""}
                aria-pressed={addressConfirmed}
                onClick={verifyAddressWithGps}
                disabled={locationCheck.status === "checking"}
              >
                <span>✓</span>
                <div>
                  <b>{addressConfirmed ? t("addressConfirmed") : locationCheck.status === "checking" ? t("checkingCurrentLocation") : t("checkAddressWithGps")}</b>
                  <small>{addressConfirmed ? t("locationVerifiedForOrder") : t("confirmAddressHelp")}</small>
                </div>
              </button>

              {locationCheck.status === "matched" && (
                <div className="address-location-check is-success">✓ {t("gpsMatchesDeliveryAddress")}</div>
              )}
              {locationCheck.status === "mismatch" && (
                <div className="address-location-check is-warning" role="alert">
                  <b>{t("youMayHaveMoved")}</b>
                  <p>{t("currentLocationDistance").replace("{distance}", locationCheck.distance.toFixed(1))}</p>
                  <div>
                    <button type="button" onClick={adjustAddressFromCurrentGps}>{t("useCurrentGpsAndAdjust")}</button>
                    <button type="button" onClick={confirmSelectedAddressAnyway}>{t("deliverToSelectedAddress")}</button>
                  </div>
                </div>
              )}
              {locationCheck.status === "imprecise" && (
                <div className="address-location-check is-warning" role="alert">
                  <b>{t("gpsNeedsConfirmation")}</b>
                  <p>{t("gpsAccuracyTooLow").replace("{accuracy}", Math.round(locationCheck.accuracy))}</p>
                  <div>
                    <button type="button" onClick={adjustAddressFromCurrentGps}>{t("reviewLocationOnMap")}</button>
                    <button type="button" onClick={confirmSelectedAddressAnyway}>{t("deliverToSelectedAddress")}</button>
                  </div>
                </div>
              )}
              {locationCheck.status === "unavailable" && (
                <div className="address-location-check is-warning" role="alert">
                  <b>{t("gpsCouldNotVerify")}</b>
                  <p>{locationCheck.reason}</p>
                  <div>
                    <button type="button" onClick={openAddress}>{t("reviewLocationOnMap")}</button>
                    <button type="button" onClick={confirmSelectedAddressAnyway}>{t("deliverToSelectedAddress")}</button>
                  </div>
                </div>
              )}
            </section>
          )}

          <button
            className="legacy-checkout-button"
            type="button"
            disabled={!isPickup && !addressConfirmed}
            onClick={continueToPayment}
          >
            {t("continuePayment")}
          </button>
        </div>
      )}
      <BottomNav />
      <AddressSheet />
    </main>
  );
}
